import {
  apiV2DevicePagePost,
  apiV2UetThermalPowerStationGeneratorUnitBindDevicePost,
} from '@maxtropy/device-customer-apis-v2';
import { CustomFilter, EllipsisSpan, Form, Input, Modal, Paging, Table, usePaging } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { FC, useEffect, useState } from 'react';
import { GeneratorUnitItem, ThermalPowerStationItem } from '../types';

const INTELLIGENT_METER_TYPE_ID = 5002;

interface Props {
  open: boolean;
  station: ThermalPowerStationItem;
  generatorUnit: GeneratorUnitItem;
  onCancel: () => void;
  onSuccess: () => void;
}

interface FilterParams {
  codeOrName?: string;
}

const columns = [
  {
    title: '设备编号',
    dataIndex: 'code',
    ellipsis: { showTitle: true },
    render: (value?: string) => <EllipsisSpan value={value || '-'} />,
  },
  {
    title: '设备名称',
    dataIndex: 'name',
    ellipsis: { showTitle: true },
    render: (value?: string) => <EllipsisSpan value={value || '-'} />,
  },
  {
    title: '所属类目',
    dataIndex: 'typeName',
    ellipsis: { showTitle: true },
    render: (value?: string) => <EllipsisSpan value={value || '-'} />,
  },
  {
    title: '所属组织',
    dataIndex: 'customerName',
    ellipsis: { showTitle: true },
    render: (value?: string) => <EllipsisSpan value={value || '-'} />,
  },
];

const DeviceModal: FC<Props> = ({ open, station, generatorUnit, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const pagingInfo = usePaging();
  const { pageOffset, pageSize, setPageOffset, setTotalCount } = pagingInfo;
  const [filters, setFilters] = useState<FilterParams>({});
  const initialDeviceId = generatorUnit.linkedDevice?.id ?? null;
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(initialDeviceId);

  useEffect(() => {
    if (open) {
      setSelectedDeviceId(initialDeviceId);
      setFilters({});
      setPageOffset(1);
    }
  }, [initialDeviceId, open, setPageOffset]);

  const { data, loading } = useRequest(
    async () => {
      const res = await apiV2DevicePagePost({
        page: pageOffset,
        size: pageSize,
        codeOrName: filters.codeOrName,
        typeId: [INTELLIGENT_METER_TYPE_ID],
        ouId: station.ou?.id,
      });
      setTotalCount(res.total ?? 0);
      return res.list ?? [];
    },
    {
      ready: open,
      refreshDeps: [filters.codeOrName, open, pageOffset, pageSize, station.ou?.id],
    }
  );

  const { runAsync, loading: confirmLoading } = useRequest(
    (deviceId: number | null) => {
      // 接口文档约定 null 表示取消关联，当前生成类型尚未包含 null。
      const params = { id: generatorUnit.id, deviceId } as Parameters<
        typeof apiV2UetThermalPowerStationGeneratorUnitBindDevicePost
      >[0];
      return apiV2UetThermalPowerStationGeneratorUnitBindDevicePost(params);
    },
    { manual: true }
  );

  const handleOk = () => {
    runAsync(selectedDeviceId).then(onSuccess);
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="关联设备"
      open={open}
      width={1000}
      destroyOnClose
      confirmLoading={confirmLoading}
      okButtonProps={{ disabled: selectedDeviceId === initialDeviceId }}
      onCancel={handleCancel}
      onOk={handleOk}
    >
      <CustomFilter<FilterParams>
        form={form}
        colSpan={8}
        onFinish={values => {
          setFilters(values as FilterParams);
          setPageOffset(1);
        }}
        onReset={() => {
          form.resetFields();
          setFilters({});
          setPageOffset(1);
        }}
      >
        <Form.Item name="codeOrName" label="编号/名称">
          <Input allowClear maxLength={50} placeholder="请输入设备名称或编号" />
        </Form.Item>
        <Form.Item label="设备类目">智能电表</Form.Item>
      </CustomFilter>
      <Table
        loading={loading}
        rowKey="id"
        columns={columns}
        dataSource={data}
        pagination={false}
        scroll={{ y: 400 }}
        rowSelection={{
          type: 'radio',
          hideSelectAll: true,
          preserveSelectedRowKeys: true,
          selectedRowKeys: selectedDeviceId === null ? [] : [selectedDeviceId],
          onChange: keys => {
            setSelectedDeviceId(keys.length ? Number(keys[0]) : null);
          },
        }}
      />
      <Paging pagingInfo={pagingInfo} />
    </Modal>
  );
};

export default DeviceModal;
